import React, { useEffect, useState } from 'react';
import { Loader2, Save } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { base44 } from '@/api/base44Client';
import { useToast } from '@/components/ui/use-toast';

// Where eBay money lands in Sage. eBay banks its own takings and keeps its
// commission before paying out, so eBay sales clear against a holding account
// of their own and the fee is journalled off it — leaving the holding account
// at zero against the payout that reaches the bank.
export default function EbaySageRoutingCard() {
  const [record, setRecord] = useState(null);
  const [clearing, setClearing] = useState('1215');
  const [fees, setFees] = useState('7900');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    (async () => {
      try {
        const list = await base44.entities.InvoiceSettings.filter({ setting_key: 'global' });
        const rec = list?.[0] || null;
        setRecord(rec);
        if (rec) {
          setClearing(rec.ebay_clearing_nominal || '1215');
          setFees(rec.ebay_fees_nominal || '7900');
        }
      } catch (e) {
        toast({ variant: 'destructive', description: e.message });
      } finally {
        setLoading(false);
      }
    })();
  }, [toast]);

  const save = async () => {
    setSaving(true);
    try {
      const patch = {
        ebay_clearing_nominal: clearing.trim() || '1215',
        ebay_fees_nominal: fees.trim() || '7900',
      };
      if (record) {
        await base44.entities.InvoiceSettings.update(record.id, patch);
      } else {
        const created = await base44.entities.InvoiceSettings.create({
          setting_key: 'global',
          ...patch,
        });
        setRecord(created);
      }
      toast({ description: 'eBay Sage routing saved.' });
    } catch (e) {
      toast({ variant: 'destructive', description: e.message });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-[#111115] border border-white/10 p-6 flex justify-center">
        <Loader2 className="w-5 h-5 text-primary animate-spin" />
      </div>
    );
  }

  return (
    <div className="bg-[#111115] border border-white/10 p-6">
      <h3 className="text-sm font-semibold text-white uppercase tracking-wider">Sage Routing</h3>
      <p className="text-xs text-white/50 mt-1">
        eBay sales clear against the holding account below rather than Square's, which eBay money
        never touches. The fee nominal takes eBay's commission off that holding account, so it nets
        to zero against the payout that reaches the bank.
      </p>
      <div className="grid sm:grid-cols-2 gap-4 mt-4 max-w-xl">
        <div>
          <label className="text-[10px] font-bold tracking-widest text-white/40 block mb-1">
            EBAY CLEARING NOMINAL
          </label>
          <input
            className="seo-input"
            value={clearing}
            onChange={(e) => setClearing(e.target.value)}
            placeholder="1215"
          />
        </div>
        <div>
          <label className="text-[10px] font-bold tracking-widest text-white/40 block mb-1">
            EBAY FEES NOMINAL
          </label>
          <input
            className="seo-input"
            value={fees}
            onChange={(e) => setFees(e.target.value)}
            placeholder="7900"
          />
        </div>
      </div>
      <Button
        onClick={save}
        disabled={saving}
        className="bg-primary hover:bg-primary/90 mt-4"
        size="sm"
      >
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
        Save
      </Button>
    </div>
  );
}